import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Alert, 
    Button, 
    Card, 
    CardContent, 
    Grid, 
    Typography, 
    useMediaQuery, 
    useTheme,
    Fade,
    Slide,
    Grow,
    Box,
    Stack,
    Container,
    Avatar,
    Divider,
    styled,
    Paper
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    RocketLaunch as RocketIcon,
    Security as SecurityIcon,
    Analytics as AnalyticsIcon,
    School as SchoolIcon,
    Group as GroupIcon,
    TrendingUp as TrendingUpIcon,
    Login as LoginIcon,
    PersonAdd as PersonAddIcon,
    Star as StarIcon,
    EmojiEvents as TrophyIcon,
    WorkspacePremium as PremiumIcon
} from '@mui/icons-material';

// Styled component for the primary button
const PrimaryButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: '14px 32px',
    fontWeight: 'bold',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
    textTransform: 'none',
    fontSize: '1.1rem',
    '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        boxShadow: '0 12px 35px rgba(76, 175, 80, 0.4)',
        transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginBottom: '12px'
    }
}));

// Styled component for the secondary button
const SecondaryButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
    color: theme.palette.secondary.contrastText,
    padding: '14px 32px',
    fontWeight: 'bold',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(156, 39, 176, 0.3)',
    textTransform: 'none',
    fontSize: '1.1rem',
    '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
        boxShadow: '0 12px 35px rgba(156, 39, 176, 0.4)',
        transform: 'translateY(-2px)'
    },
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
        width: '100%'
    }
}));

// Feature Card Component
const FeatureCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    borderRadius: '16px',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
        borderColor: theme.palette.primary.main
    }
}));

const GuestUser = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        // Animation trigger or any side effects
    }, []);

    const features = [
        {
            icon: <AnalyticsIcon color="primary" sx={{ fontSize: 40 }} />,
            title: "Advanced Analytics",
            description: "Track research impact and engagement metrics"
        },
        {
            icon: <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />,
            title: "Academic Network",
            description: "Connect with researchers worldwide"
        },
        {
            icon: <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />,
            title: "Research Growth",
            description: "Monitor your academic progress and citations"
        },
        {
            icon: <GroupIcon color="info" sx={{ fontSize: 40 }} />,
            title: "Collaboration Tools",
            description: "Work seamlessly with research teams"
        }
    ];

    const benefits = [
        { text: "Access to premium research tools", icon: <PremiumIcon color="primary" /> },
        { text: "Personalized research dashboard", icon: <StarIcon color="warning" /> },
        { text: "Advanced publication analytics", icon: <AnalyticsIcon color="info" /> },
        { text: "Collaboration opportunities", icon: <GroupIcon color="success" /> }
    ];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
            >
                {/* Main Welcome Card */}
                <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                    <Card sx={{
                        width: '100%',
                        borderRadius: '20px',
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: 'hidden',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                        }
                    }}>
                        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                            <Grid container spacing={4} alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <Fade in={true} timeout={800}>
                                        <Stack spacing={3}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ 
                                                    bgcolor: 'primary.main', 
                                                    width: 60, 
                                                    height: 60,
                                                    boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)'
                                                }}>
                                                    <RocketIcon sx={{ fontSize: 30 }} />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
                                                        Welcome to RIAUG
                                                    </Typography>
                                                    <Typography variant="h6" color="text.secondary">
                                                        Research & Innovation Academy
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Alert severity="info" sx={{
                                                borderRadius: '12px',
                                                borderLeft: `4px solid ${theme.palette.info.main}`,
                                                background: `linear-gradient(135deg, ${theme.palette.info.light}15 0%, transparent 100%)`
                                            }}>
                                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <SecurityIcon color="info" />
                                                    Unlock Premium Features
                                                </Typography>
                                                <Typography variant="body1" paragraph>
                                                    Join our academic community to access advanced research tools, 
                                                    collaboration opportunities, and personalized analytics.
                                                </Typography>
                                            </Alert>

                                            {/* Benefits List */}
                                            <Stack spacing={1}>
                                                {benefits.map((benefit, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.1 }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar sx={{ 
                                                                bgcolor: 'primary.light', 
                                                                width: 32, 
                                                                height: 32,
                                                                color: 'primary.main'
                                                            }}>
                                                                {benefit.icon}
                                                            </Avatar>
                                                            <Typography variant="body1" color="text.primary">
                                                                {benefit.text}
                                                            </Typography>
                                                        </Box>
                                                    </motion.div>
                                                ))}
                                            </Stack>
                                        </Stack>
                                    </Fade>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Grow in={true} timeout={1000}>
                                        <Box sx={{ 
                                            textAlign: 'center',
                                            p: 3,
                                            borderRadius: '16px',
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(10px)'
                                        }}>
                                            <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                                                Get Started Today
                                            </Typography>
                                            <Typography variant="body1" color="text.secondary" paragraph>
                                                Join thousands of researchers already using RIAUG
                                            </Typography>

                                            <Stack spacing={2} sx={{ mt: 3 }}>
                                                <PrimaryButton
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    startIcon={<LoginIcon />}
                                                    component={Link}
                                                    to="/"
                                                >
                                                    Sign In to Your Account
                                                </PrimaryButton>
                                                
                                                <SecondaryButton
                                                    fullWidth
                                                    variant="contained"
                                                    size="large"
                                                    startIcon={<PersonAddIcon />}
                                                    component={Link}
                                                    to="/register"
                                                >
                                                    Create New Account
                                                </SecondaryButton>
                                            </Stack>

                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                                Free registration • No credit card required
                                            </Typography>
                                        </Box>
                                    </Grow>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Slide>

                {/* Features Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                >
                    <Typography variant="h4" component="h2" textAlign="center" sx={{ mt: 6, mb: 4 }} fontWeight="bold">
                        Why Join RIAUG?
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mb: 6 }}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <FeatureCard>
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: index * 0.1 + 0.6 }}
                                        >
                                            {feature.icon}
                                        </motion.div>
                                        <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }} fontWeight="bold">
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {feature.description}
                                        </Typography>
                                    </FeatureCard>
                                </motion.div>
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                {/* Stats Section */}
                <Fade in={true} timeout={1200}>
                    <Paper sx={{ 
                        p: 4, 
                        textAlign: 'center', 
                        borderRadius: '16px',
                        border: `1px solid ${theme.palette.divider}`
                    }}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <TrophyIcon color="warning" sx={{ fontSize: 40 }} />
                                    <Box textAlign="left">
                                        <Typography variant="h4" component="div" fontWeight="bold" color="primary">
                                            10K+
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Active Researchers
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />
                                    <Box textAlign="left">
                                        <Typography variant="h4" component="div" fontWeight="bold" color="primary">
                                            500+
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Universities
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                    <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                                    <Box textAlign="left">
                                        <Typography variant="h4" component="div" fontWeight="bold" color="primary">
                                            50K+
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Research Papers
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Fade>

                {/* Final CTA */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                    style={{ marginTop: '40px', textAlign: 'center' }}
                >
                    <Typography variant="body1" color="text.secondary" paragraph>
                        Ready to transform your research journey?
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                        <PrimaryButton
                            variant="contained"
                            size="large"
                            startIcon={<LoginIcon />}
                            component={Link}
                            to="/"
                        >
                            Sign In Now
                        </PrimaryButton>
                        <SecondaryButton
                            variant="contained"
                            size="large"
                            startIcon={<PersonAddIcon />}
                            component={Link}
                            to="/register"
                        >
                            Join Our Community
                        </SecondaryButton>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Need assistance? Contact us at support@riaug.com
                    </Typography>
                </motion.div>
            </motion.div>
        </Container>
    );
};

export default GuestUser;